import { Box, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CanvasAddElementMenu } from './CanvasAddElementMenu';
import type { CanvasSlot } from './CanvasStructureContext';

export interface CanvasInsertionLineProps {
  slot: CanvasSlot;
  edge: 'top' | 'bottom';
  /** Names the neighbour, e.g. "Insert element after Customer name". */
  label: string;
  testId: string;
}

/**
 * The hover insertion line between two elements (Figma `be.canvas.insert`): a thin line with a
 * centred "+" that opens the add-element menu for the gap it sits in. Absolutely positioned on the
 * element's edge, so it never adds height to the page; it shows on hover and on keyboard focus.
 */
export function CanvasInsertionLine({ slot, edge, label, testId }: CanvasInsertionLineProps) {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        [edge]: -7,
        height: 14,
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        // The strip is invisible until the element is hovered or the "+" is focused. It must not
        // catch clicks while hidden — it overlaps the next element's settings tab.
        pointerEvents: 'none',
        transition: 'opacity 120ms',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
      }}
      data-insertion-line=""
      data-testid={testId}
    >
      <Box aria-hidden sx={{ position: 'absolute', left: 0, right: 0, top: 6, height: 2, bgcolor: 'var(--border-focus)' }} />
      <CanvasAddElementMenu
        slot={slot}
        testId={testId}
        renderTrigger={({ onClick, expanded }) => (
          <IconButton
            size="small"
            aria-label={label}
            aria-haspopup="menu"
            aria-expanded={expanded}
            onClick={onClick}
            data-testid={`${testId}:button`}
            sx={{
              width: 16,
              height: 16,
              p: 0,
              pointerEvents: 'auto',
              bgcolor: 'var(--border-focus)',
              color: 'var(--icon-on-color)',
              '&:hover': { bgcolor: 'var(--border-focus)' }
            }}
          >
            <AddIcon sx={{ fontSize: 12 }} />
          </IconButton>
        )}
      />
    </Box>
  );
}
